import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MomentCore from 'moment';

import {
	makeStyles,
	useMediaQuery,
	useTheme,
	Grid,
	Divider
} from '@material-ui/core';

//import Fido from '../../utils/Fido';
import Constants from "../../utils/Constants";
import AuthorizedUser from "../../utils/AuthorizedUser";
import AuthorizedUserContext from "../../utils/AuthorizedUserContext";
import {useFilterHandler, useStickyFilterOptions, useStickyFilterHandler} from "../../utils/FilterHooks";
import FilterSelect from "../../components/FilterSelect";
import AccountFilterField from "../../components/AccountFilterField";
import DateRangePicker from "../../components/DateRangePicker";

const styles = makeStyles(theme => ({
	root: {
		padding: theme.spacing(2),

		[theme.breakpoints.up('sm')]: {
			padding: theme.spacing(4)
		},

		[theme.breakpoints.up('md')]: {
			paddingTop: theme.spacing(2),
			paddingBottom: theme.spacing(6),
			paddingLeft: theme.spacing(8),
			paddingRight: theme.spacing(8)
		},

		// added previousDays into chart cards so needs less padding
		// in between certain breakpoints so text / chart doesn't break out of cards
		[theme.breakpoints.up('lg')]: {
			paddingLeft: theme.spacing(3),
			paddingRight: theme.spacing(3)
		},

		/*[theme.breakpoints.up(1070)]: {
			padding: theme.spacing(6)
		},*/

		[theme.breakpoints.up('xl')]: {
			paddingTop: theme.spacing(2),
			paddingBottom: theme.spacing(6),
			paddingLeft: theme.spacing(5),
			paddingRight: theme.spacing(5)
		}
	},

	sparkCardGridItem: {
		paddingTop: "0px !important"
	},

	sparkCardContainer: {
		/* "& div:last-child": {
			"& div": {
				borderRight: 0
			}
		} */
	},

	dividerItem: {
		paddingBottom: 0
	},

	divider: {
		height: 2
	},

	filterGrid: {
		paddingLeft: `${theme.spacing(1)} !important`
	},

	clock: {
		fontSize: theme.spacing(2)
	},

	accountSelectField: {
		marginLeft: theme.spacing(1)
	}
}));

const dateFormat = "YYYY-MM-DD";
function OverviewReportLayout(props) {
	const theme = useTheme();
	const filterHandlerHook = useFilterHandler();
	const authenticatedUser = useContext(AuthorizedUserContext);

	const stickyFilterHandler = useStickyFilterHandler();
	const defaultFilterAccounts = useStickyFilterOptions(
		AuthorizedUser.hasStickyAccountFilter(authenticatedUser),
		authenticatedUser,
		AccountFilterField.stickyLocalStorageKey,
		authenticatedUser.childAccounts
	);

	const getDates = (previousDays, dates) => {
		let newDates;

		if (previousDays == null && dates.startDate && dates.endDate) {
			newDates = {
				sDate: (new MomentCore(dates.startDate)).format(dateFormat),
				eDate: (new MomentCore(dates.endDate)).format(dateFormat)
			};
		} else {
			newDates = {
				sDate: ((new MomentCore()).subtract(previousDays - 1, "days")).format(dateFormat),
				eDate: (new MomentCore()).format(dateFormat)
			};
		}

		return newDates;
	};

	const [accounts, setAccounts] = useState(defaultFilterAccounts);
	const [filter, setFilter] = useState({
		...getDates(props.previousDays),
		previousDays: [props.previousDays],
		account: (defaultFilterAccounts).map(opt => opt.id)
	});

	const atLeastMD = useMediaQuery(theme.breakpoints.up("md"), {noSsr: true}),
		atLeastSM = useMediaQuery(theme.breakpoints.up("sm"), {noSsr: true});

	let classes = styles(),
		fullWidthSizing = {xs: 12, sm: 12, md: 12, lg: 12, xl: 12},
		biColumnSizing = {xs: 12, sm: 12, md: 6, lg: 6, xl: 6},
		sparkCardSizing = {xs: 12, sm: 4, md: 4, lg: 4, xl: 2},
		{sparkCards, children, filterGridItemClassName,
			showAccounts, extraFilterFields, filterCallback, showPreviousDays, forcePreviousDays, daysOptions} = props;

	const clearDataTables = () => {
		document.querySelectorAll("[class='highcharts-data-table']").forEach(el => {el.remove();});
	};

	const filterChanged = (selected) => {
		let oldFilter = Object.assign({}, filter),
			newFilter = Object.assign({}, oldFilter, selected);

		if (JSON.stringify(oldFilter) !== JSON.stringify(newFilter)) {
			/*if (JSON.stringify(oldFilter.previousDays[0]) !== JSON.stringify(newFilter.previousDays[0])) {
				newFilter = {...newFilter, ...getDates(newFilter.previousDays[0])};
				executeFilter(newFilter);
			}*/
			executeFilter(newFilter);
		}
	};

	const accountFilterChanged = (option, name, selectAll, reset, array, defaultValue, setter, filteredSelectAll) => {
		let accounts = [],
			result = filterHandlerHook(option, name, selectAll, reset, array, defaultValue, setter, filteredSelectAll);

		result.newArray.forEach(opt => opt.selected ? accounts.push(opt.id) : null);
		filterChanged({account: accounts});
	};

	const datesChanged = (dates) => {
		filterChanged({
			...getDates(null, dates),
			previousDays: [dates.previousDays]
		});
	};

	useEffect(() => {
		filterCallback(filter);
	}, [filter, filterCallback]);

	const executeFilter = (filterObj) => {
		clearDataTables();
		setFilter(filterObj);
	};

	return (
		<div className={classes.root}>
			<Grid container spacing={4} alignItems="flex-start">
				<Grid item {...fullWidthSizing} className={filterGridItemClassName}>
					<Grid
						container
						className={classes.filterGrid}
						justify="flex-start">
						{(!atLeastMD || forcePreviousDays) && showPreviousDays ?
							<Grid item>
								<FilterSelect
									singleSelect
									name="previousDays"
									options={daysOptions}
									defaultSelections={filter.previousDays}
									menuTitle={`${filter.previousDays[0]} Days`}
									ariaLabel="Previous days selector"
									handleUpdate={days => filterChanged({...days, ...getDates(days.previousDays[0])})}
									startIcon={<FontAwesomeIcon className={classes.clock} icon={["far", "clock"]} />} />
							</Grid> : null
						}
						{atLeastMD && showPreviousDays && !forcePreviousDays ?
							<Grid item>
								<DateRangePicker
									initialDateRange={{startDate: filter.sDate, endDate: filter.eDate}}
									minDate={((new MomentCore()).subtract(1, "years")).format(dateFormat)} // 1yr ago
									maxDate={(new MomentCore()).format(dateFormat)} // today
									updateHandler={datesChanged} />
							</Grid> : null
						}
						{showAccounts ?
							<Grid item>
								<AccountFilterField
									includeReset={atLeastSM}
									accountOptions={accounts}
									className={classes.accountSelectField}
									changeHandler={(option, name, selectAll, reset, filteredSelectAll) => {
										accountFilterChanged(option, name, selectAll, reset, accounts, null, setAccounts, filteredSelectAll);
									}}
									stickyFilterHandler={(localStorageKey, isSticky) => stickyFilterHandler(authenticatedUser, accounts, localStorageKey, isSticky)} />
							</Grid> : null
						}
						{extraFilterFields && extraFilterFields.length ?
							extraFilterFields.map((field, idx) => {
								return (
									<Grid item key={idx}>
										{field}
									</Grid>
								);
							}) : null
						}
					</Grid>
				</Grid>
				{sparkCards && sparkCards.length ?
					<Grid item {...fullWidthSizing} className={classes.sparkCardGridItem}>
						<Grid container
							tabIndex={0}
							role="group"
							aria-hidden="false"
							justifyContent="center"
							alignItems="flex-start"
							className={classes.sparkCardContainer}>
							{sparkCards.map((card, idx) => {
								return <Grid item key={`key-${idx}`} {...sparkCardSizing}>{card}</Grid>;
							})}
						</Grid>
					</Grid> : null
				}
				{children.length > 0 ? children.map((child, idx) => {
					return child ?
						child.props.divider ?
							<Grid
								item
								key={`key-${idx}`} {...fullWidthSizing}
								className={classes.dividerItem}>
								<Divider classes={{root: classes.divider}} />
							</Grid>
							:
							<Grid
								item
								className={child.props.className}
								key={`key-${idx}`} {...(child.props.fullWidth ? fullWidthSizing : biColumnSizing)}>
								{child}
							</Grid>
						: null;
				}) : null}
			</Grid>
		</div>
	);
}

OverviewReportLayout.defaultProps = {
	previousDays: 30,
	showAccounts: true,
	forcePreviousDays: false,
	showPreviousDays: true,
	daysOptions: Constants.REPORT.DAYS
};

OverviewReportLayout.propTypes = {
	previousDays: PropTypes.number.isRequired,
	//selectedAccounts: PropTypes.array,
	//filter: PropTypes.func,
	children: PropTypes.array.isRequired,
	sparkCards: PropTypes.array,
	filterCallback: PropTypes.func,
	location: PropTypes.object,
	showAccounts: PropTypes.bool,
	showPreviousDays: PropTypes.bool,
	forcePreviousDays: PropTypes.bool,
	extraFilterFields: PropTypes.array,
	filterGridItemClassName: PropTypes.string,
	daysOptions: PropTypes.array
};

export default OverviewReportLayout;
